import { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { api } from '../lib/api';
import { ErrorAlert } from '../components/ui';

type Provider = 'google' | 'line';

const PROVIDER_LABEL: Record<Provider, string> = {
  google: 'เข้าสู่ระบบด้วย Google',
  line: 'เข้าสู่ระบบด้วย LINE',
};

/**
 * เข้าระบบด้วยบัญชีที่มีอยู่แล้ว ไม่มีรหัสผ่านให้จำ
 *
 * มูลนิธิมีเจ้าหน้าที่ไม่กี่คนและไม่มีฝ่าย IT รหัสผ่านที่ตั้งเองมักถูกจดใส่กระดาษ
 * หรือใช้ซ้ำกับที่อื่น ซึ่งเสี่ยงกว่าการพึ่ง Google/LINE ที่มี 2FA ให้อยู่แล้ว
 *
 * ระบบไม่สร้างบัญชีให้อัตโนมัติ — ต้องมีเจ้าหน้าที่ที่ผูกไว้ก่อนเท่านั้น
 * ไม่งั้นใครที่มี Gmail ก็เดินเข้ามาถึงข้อมูลผู้รับความช่วยเหลือได้
 */
export default function Login() {
  const { startLogin, completeLogin } = useAuth();
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<{ kind: string; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ providers: Provider[] }>('/auth/providers')
      .then((r) => setProviders(r.providers))
      .catch(setError);
  }, []);

  // กลับมาจากหน้าของ Google/LINE — อ่านผลแล้วล้าง query ทิ้งทันที
  // โค้ดแลก token ใช้ได้ครั้งเดียวอยู่แล้ว แต่ไม่ควรค้างอยู่บนแถบที่อยู่ให้ใครเห็น
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const kind = q.get('login');
    if (!kind) return;
    const code = q.get('code');
    const detail = q.get('detail') ?? '';
    window.history.replaceState({}, '', window.location.pathname);

    if (kind === 'ok' && code) {
      setBusy(true);
      completeLogin(code)
        .catch(setError)
        .finally(() => setBusy(false));
      return;
    }
    setNotice({ kind, detail });
  }, [completeLogin]);

  async function go(provider: Provider) {
    setError(null);
    setBusy(true);
    try {
      await startLogin(provider);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="card login-card">
        <h1>ระบบบริหารจัดการมูลนิธิ</h1>
        <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
          สำหรับผู้ดูแลระบบและเจ้าหน้าที่การเงิน
        </p>

        <ErrorAlert error={error} />

        {notice?.kind === 'unknown' && (
          <div className="alert warn">
            {notice.detail || 'บัญชีนี้ยังไม่ได้ผูกกับเจ้าหน้าที่'}
            <br />
            กรุณาติดต่อผู้ดูแลระบบให้เพิ่มบัญชีของคุณก่อน
          </div>
        )}
        {notice?.kind === 'inactive' && (
          <div className="alert warn">บัญชีของคุณถูกปิดใช้งานอยู่ กรุณาติดต่อผู้ดูแลระบบ</div>
        )}
        {notice?.kind === 'denied' && (
          <div className="alert info">คุณยกเลิกการเข้าสู่ระบบ กดปุ่มด้านล่างเพื่อลองใหม่</div>
        )}
        {notice?.kind === 'error' && (
          <div className="alert error">เข้าสู่ระบบไม่สำเร็จ: {notice.detail}</div>
        )}

        {providers === null ? (
          <p className="muted small">กำลังโหลด…</p>
        ) : providers.length === 0 ? (
          <div className="alert error">
            ยังไม่ได้ตั้งค่าวิธีเข้าสู่ระบบเลยสักทาง — ผู้ดูแลระบบต้องตั้ง Google หรือ LINE Login
            ก่อน (ดู <span className="mono">docs/14-login.md</span>)
          </div>
        ) : (
          <div className="btn-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            {providers.map((p) => (
              <button
                key={p}
                className={p === 'google' ? 'btn secondary' : 'btn'}
                disabled={busy}
                onClick={() => void go(p)}
                style={{ width: '100%' }}
              >
                {busy ? 'กำลังพาไปหน้าเข้าสู่ระบบ…' : PROVIDER_LABEL[p]}
              </button>
            ))}
          </div>
        )}

        <p className="muted small" style={{ marginBottom: 0, marginTop: 16 }}>
          เจ้าหน้าที่ภาคสนามใช้งานผ่าน LINE ไม่ต้องเข้าหน้านี้
        </p>
      </div>
    </div>
  );
}
